import { createContext, useContext, useState, useEffect } from 'react'
import { authService } from '../services/api'

const AuthContext = createContext(null)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de AuthProvider')
  }
  return context
}

const ORG_SCHEMA_KEY = 'org_schema'
const ORGANIZATIONS_KEY = 'organizations'

function readStoredOrganizations() {
  try {
    const parsed = JSON.parse(localStorage.getItem(ORGANIZATIONS_KEY) || '[]')
    return Array.isArray(parsed) ? parsed : []
  } catch (error) {
    return []
  }
}

function clearPermissionCache() {
  try {
    localStorage.removeItem('wfmed_permissions_cache')
    localStorage.removeItem('wfmed_permissions_cache_timestamp')
    localStorage.removeItem('wfmed_permissions_cache_v2')
    localStorage.removeItem('wfmed_permissions_cache_timestamp_v2')
  } catch (error) {
    console.warn('[AuthContext] Erro ao limpar cache de permissões:', error)
  }
}

function storeSession({ user, token, organizations }) {
  localStorage.setItem('token', token)
  localStorage.setItem('user', JSON.stringify(user))
  localStorage.setItem(ORGANIZATIONS_KEY, JSON.stringify(organizations || []))
  if (user?.organization_schema) {
    localStorage.setItem(ORG_SCHEMA_KEY, user.organization_schema)
  }
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [organizations, setOrganizations] = useState(readStoredOrganizations)
  const [currentSchema, setCurrentSchema] = useState(() => localStorage.getItem(ORG_SCHEMA_KEY))
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Verificar se há token salvo
    const token = localStorage.getItem('token')
    const savedUser = localStorage.getItem('user')

    if (token && savedUser) {
      try {
        setUser(JSON.parse(savedUser))
        // Verificar se o token ainda é válido
        authService.getProfile()
          .then(response => {
            if (response.success) {
              console.log('[AuthContext] Dados do perfil carregados:', {
                email: response.data.email,
                role: response.data.role,
                access_group_id: response.data.access_group_id
              })
              setUser(response.data)
              localStorage.setItem('user', JSON.stringify(response.data))
              authService.organizations()
                .then((orgResponse) => {
                  const nextOrganizations = orgResponse.data?.organizations || []
                  setOrganizations(nextOrganizations)
                  setCurrentSchema(orgResponse.data?.current || null)
                  localStorage.setItem(ORGANIZATIONS_KEY, JSON.stringify(nextOrganizations))
                  if (orgResponse.data?.current) {
                    localStorage.setItem(ORG_SCHEMA_KEY, orgResponse.data.current)
                  }
                })
                .catch(() => {})
            } else {
              logout()
            }
          })
          .catch(() => {
            logout()
          })
          .finally(() => setLoading(false))
      } catch (error) {
        logout()
        setLoading(false)
      }
    } else {
      setLoading(false)
    }
  }, [])

  const applySession = (response) => {
    const { user: sessionUser, token, organizations: nextOrganizations } = response.data
    storeSession({ user: sessionUser, token, organizations: nextOrganizations })
    setOrganizations(nextOrganizations || [])
    setCurrentSchema(sessionUser.organization_schema || null)
    setUser(sessionUser)
    return { success: true, user: sessionUser }
  }

  const login = async (email, password) => {
    try {
      const response = await authService.login(email, password)
      if (response.success) {
        console.log('[AuthContext] Login realizado:', {
          email: response.data.user.email,
          role: response.data.user.role,
          access_group_id: response.data.user.access_group_id
        })
        return applySession(response)
      }
      return { success: false, message: response.message }
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Erro ao fazer login'
      }
    }
  }

  const loginWithGoogle = async (credential) => {
    try {
      const response = await authService.loginWithGoogle(credential)
      if (response.success) return applySession(response)
      return { success: false, message: response.message }
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Não foi possível entrar com o Google'
      }
    }
  }

  const register = async (userData) => {
    try {
      const response = await authService.register(userData)
      if (response.success) {
        const { user, token } = response.data
        localStorage.setItem('token', token)
        localStorage.setItem('user', JSON.stringify(user))
        setUser(user)
        return { success: true }
      }
      return { success: false, message: response.message, errors: response.errors }
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Erro ao registrar',
        errors: error.response?.data?.errors
      }
    }
  }

  const logout = () => {
    localStorage.removeItem('token')
    localStorage.removeItem('user')
    localStorage.removeItem(ORG_SCHEMA_KEY)
    localStorage.removeItem(ORGANIZATIONS_KEY)
    clearPermissionCache()
    setOrganizations([])
    setCurrentSchema(null)
    setUser(null)
  }

  const switchOrganization = async (schema) => {
    try {
      const response = await authService.switchOrganization(schema)
      if (!response.success) {
        throw new Error(response.message || 'Não foi possível trocar de organização')
      }
      storeSession(response.data)
      clearPermissionCache()
      window.location.assign('/erp')
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message || 'Não foi possível trocar de organização')
    }
  }

  const updateUser = (updatedUser) => {
    setUser(updatedUser)
    localStorage.setItem('user', JSON.stringify(updatedUser))
  }

  const isAdmin = () => {
    return user?.role === 'admin'
  }

  const canAccessERP = () => {
    if (!user) return false
    // Admin sempre tem acesso
    if (user.role === 'admin') return true
    // Usuário comum precisa ter access_group_id válido
    if (user.role === 'user') {
      return !!user.access_group_id
    }
    return false
  }

  const value = {
    user,
    loading,
    organizations,
    currentSchema,
    login,
    loginWithGoogle,
    register,
    logout,
    switchOrganization,
    updateUser,
    isAdmin,
    canAccessERP,
    isAuthenticated: !!user
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

